/**
 * teacherCalendar — часовете и ваканциите на учителя, разгънати по КАЛЕНДАРНИ
 * ДАТИ за `CalendarWidget`.
 *
 * Слотът е седмично правило („вторник 16:00"), а календарът иска конкретни дни.
 * Разгъването живее тук, а не в страницата, защото носи две решения, които
 * трябва да са на едно място:
 *
 *  1. **Часовете отпадат в неучебни дни.** Ваканцията се разгъва ПЪРВА и
 *     нейните дати потискат съвпадащите часове — иначе календарът обещава час,
 *     който няма да се проведе.
 *  2. **Границите на учебната година** се извеждат от подадената дата, не от
 *     `new Date()` вътре. Функция, която чете часовника сама, дава различен
 *     резултат според деня на пускане, тоест тестът ѝ или е плаващ, или е
 *     написан така, че да мине.
 *
 * Нарочно БЕЗ i18n зависимост: текстовете влизат като параметри, за да може
 * тестът да асертва върху дати и брой, не върху преводи.
 */

import type { TeacherCalendarBounds } from '@shared/types';
import type { CalendarEvent } from '../components/ui/CalendarWidget';
import type { TeacherScheduleSlotView, TeacherHolidayView } from '@shared/types';
import type { NonWorkingRange } from '@shared/utils/payroll/calendar';
import {
  lessonDatesForSlot,
  dateWindowFrom,
  clipRangeToWindow,
  schoolYearStartDate,
  schoolYearKeyFromDate,
  absoluteWorkYearWindow,
  resolveSlotPlan,
  type DateWindow,
} from '@shared/utils/schoolCalendar';
import { getDatesForDayOfWeek } from './brandColors';
import {
  calendarPalette,
  markShape,
  nonWorkingRole,
  type CalendarPalette,
  type CalendarRole,
} from './calendarPalette';

export interface TeacherCalendarOptions {
  /**
   * Брандовият primary — ЕДИНСТВЕНИЯТ цвят, който идва от училището. Останалите се
   * извеждат от `calendarPalette`, защото семантичен цвят, взет от бранда, се сблъсква
   * с друг семантичен цвят при първото училище с подходящ бранд (случи се на 2026-09-05:
   * secondary #dc2626 == цвета на празника).
   */
  primaryColor?: string | null;
  /** Имената на трите вида неучебни дни — етикетът носи типа, не само името. */
  kindLabels?: Partial<Record<'vacation' | 'holiday' | 'noSchool', string>>;
  /** Отправна дата за границите на учебната година. Подава се изрично. */
  today: Date;
  /** Добавя се към етикета на помощническите часове, напр. „(помощник)". */
  assistantSuffix?: string;
  /** Добавя се към етикета на ПОСЛЕДНОТО занятие на слота, напр. „(последен час)". */
  lastLessonSuffix?: string;
  /**
   * Етикет за слот без нито едно занятие в периода (R9). Липсва → маркерът пак се рисува,
   * само че с голото име на слота: по-добре знак без обяснение, отколкото празнота.
   */
  noLessonsSuffix?: string;
  /**
   * Учебната година, за която сървърът е върнал слотовете и ваканциите
   * („2026-2027"). Прозорецът на календара се извежда от НЕЯ; `today` остава
   * резерв за стар backend, който още не я връща.
   */
  schoolYear?: string | null;
  /**
   * Границите, РЕШЕНИ НА СЪРВЪРА (C4 R5) — този модул не ги смята.
   *
   * Липсват при стар backend; тогава се пада на екранния прозорец, вместо календарът да
   * изчезне. Мярката на R5 е проверима в кода: тук няма извикване на гранична функция.
   */
  bounds?: TeacherCalendarBounds;
}

/**
 * Локален `Date` → ISO "YYYY-MM-DD" по ЛОКАЛНИТЕ му части.
 *
 * `toISOString()` тук би било грешка: `schoolYearBounds` конструира локална
 * полунощ, а изместването към UTC би дало 31 август вместо 1 септември за всяка
 * зона източно от Гринуич — тоест едно занятие в повече, според машината.
 */
export function toIsoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * Границите на учебната година, изведени от дата: септември → юни.
 *
 * Септември е месец 8 (0-базиран). От септември нататък годината започва в
 * текущата календарна; преди това — в миналата.
 */
export function schoolYearBounds(today: Date): { start: Date; end: Date } {
  // Правилото живее в `schoolYearKeyFromDate` (shared) — там го чете и резервът на
  // `useActiveSchoolYear`. Две копия биха се разминали при първата промяна на границата.
  const startYear = Number(schoolYearKeyFromDate(today).slice(0, 4));
  return {
    start: new Date(startYear, 8, 1),        // 1 септември
    end: new Date(startYear + 1, 5, 30),     // 30 юни
  };
}

/** Разгъва един неучебен период до множеството ISO дати, които покрива. */
export function expandHolidayDates(holiday: Pick<TeacherHolidayView, 'startDate' | 'endDate'>): string[] {
  const [sy, sm, sd] = holiday.startDate.split('-').map(Number);
  const [ey, em, ed] = holiday.endDate.split('-').map(Number);
  if (!sy || !sm || !sd || !ey || !em || !ed) return [];
  const cursor = new Date(Date.UTC(sy, sm - 1, sd));
  const last = new Date(Date.UTC(ey, em - 1, ed));
  if (cursor.getTime() > last.getTime()) return [];
  const out: string[] = [];
  // Таван: една учебна година. Обърнат/повреден запис не бива да върти вечно.
  for (let guard = 0; cursor.getTime() <= last.getTime() && guard < 400; guard++) {
    const m = String(cursor.getUTCMonth() + 1).padStart(2, '0');
    const d = String(cursor.getUTCDate()).padStart(2, '0');
    out.push(`${cursor.getUTCFullYear()}-${m}-${d}`);
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return out;
}

/** Неучебен период, както го подава извикващият: ISO дати + как да се покаже. */
export interface NonWorkingSource {
  startDate: string;
  endDate: string;
  name: string;
  /** Типът, както го пази Firestore: `vacation` | `holiday` | `no_school`. */
  kind: string;
}

/** Клипнат неучебен период — входът на изчислителя И на маркера. */
export interface NonWorkingPeriod {
  range: NonWorkingRange;
  name: string;
  kind: string;
}

/**
 * Неучебните периоди, КЛИПНАТИ към прозореца на календара.
 *
 * Отделна функция, а не вътрешна променлива, защото това е ЕДИНСТВЕНИЯТ вход на
 * „в този ден час няма": маркерите, потискането и краят на курса се извеждат от
 * върнатия масив. Задача E добавя тук тържествата с „часовете отпадат" — на
 * едно място, вместо да гони три разминаващи се списъка.
 *
 * Клипването не е козметика: без него един и същ повреден запис стига до трите
 * консуматора в пълната си дължина, а те го съкращават по различни правила и
 * после показват различни неучебни дни (вж. `clipRangeToWindow`).
 *
 * Затова я вика и `AdminPage` — въпреки името на файла тя не е „учителска".
 * Админският календар чете ваканциите като `Timestamp` и ги нормализира до ISO
 * преди да я извика.
 */
export function nonWorkingPeriodsFrom(
  periods: NonWorkingSource[],
  window: DateWindow,
): NonWorkingPeriod[] {
  const out: NonWorkingPeriod[] = [];
  for (const p of periods) {
    const range = clipRangeToWindow({ startDate: p.startDate, endDate: p.endDate }, window);
    if (range) out.push({ range, name: p.name, kind: p.kind });
  }
  return out;
}

/**
 * Маркерът на неучебен период: цвят, форма и етикет с ТИПА.
 *
 * Типът влиза в етикета винаги, дори когато името вече го съдържа („Коледна ваканция ·
 * ваканция"). Тавтологията е нарочна: етикетът е резервният канал за човек, който не
 * различава цветовете, а правило „типът само когато не се съдържа в името" е точно
 * тихата грешка — при ново име мълчаливо изчезва обяснението.
 *
 * Общ е за двете табла: `AdminPage` рисува същите периоди и без него би държал втора
 * палитра, която вече веднъж дрейфна.
 */
export function nonWorkingEvent(
  period: NonWorkingPeriod,
  palette: CalendarPalette,
  kindLabels?: Partial<Record<'vacation' | 'holiday' | 'noSchool', string>>,
): Omit<CalendarEvent, 'date'> {
  const role = nonWorkingRole(period.kind);
  const kindLabel = kindLabels?.[role as 'vacation' | 'holiday' | 'noSchool'];
  return {
    label: kindLabel ? `${period.name} · ${kindLabel}` : period.name,
    color: palette[role],
    shape: markShape(role),
  };
}

/**
 * Датите на резервния път — когато планът по брой занятия НЕ важи.
 *
 * 🔴 ЕДНА функция за двата екрана. Цикълът беше преписан инлайн и в `AdminPage`, и тук, и
 * трите му правила се разминаха: прагът (началото на слота срещу началото на прозореца),
 * преливането на `setMonth` и горната граница. Резултатът беше един и същ слот с различно
 * първо занятие на учителското и на админското табло.
 *
 * Правилата, всичките измерени, не предположени:
 *  · курсорът тръгва от ПЪРВИ ден на месеца — `setMonth(+1)` прелива, ако денят не
 *    съществува в следващия месец (от 31 август следващата стъпка е 1 октомври, тоест
 *    септември изчезва целият);
 *  · `getDatesForDayOfWeek` изброява ЦЕЛИЯ месец, затова датите преди началото на слота се
 *    отсяват изрично — иначе прозорец от 31 август ражда занятия в началото на август;
 *  · краят е 30 юни (поведението, което задача B закова), НО слот, чието начало пада след
 *    него, получава края на 12-месечния прозорец: иначе не произвежда нито едно занятие и
 *    изчезва без маркер — състоянието, което докблокът на модула обявява за забранено.
 */
export function fallbackLessonDates(input: {
  dayOfWeek: string;
  /** Вече решеното начало на слота (`resolveSlotPlan`). */
  slotStartIso: string;
  /**
   * Началото на ПРОЗОРЕЦА — долната граница на рисуването.
   *
   * 🔴 Без нея слот с начало преди прозореца се разгъваше от собствената си дата НАЗАД, в
   * предишната учебна година. Измерено срещу `b83f182`: начало 2025-09-15 при прозорец от
   * 2026-09-01 дава 93 точки, 50 от които преди прозореца, първата 2025-09-20. И те не са
   * просто излишни: `nonWorking` вече е клипнат към прозореца, тоест ваканциите на онази
   * година не ги потискат и нито една от тях не носи маркер, който да обясни какво е.
   */
  windowStartIso: string;
  /** Краят на резервния прозорец — 30 юни. */
  fallbackEndIso: string;
  /** Краят на 12-месечния прозорец — резервът за слот, започващ след 30 юни. */
  windowEndIso: string;
  nonWorking: NonWorkingRange[];
}): string[] {
  const { dayOfWeek, slotStartIso, windowStartIso, fallbackEndIso, windowEndIso, nonWorking } = input;
  if (!dayOfWeek) return [];

  /**
   * Краят: 30 юни за заварените слотове (поведението, което задача B закова), а слот, чието
   * начало е след него — до края на прозореца.
   *
   * 🔴 Тук ИМАШЕ клауза, която разширяваше края отвъд прозореца за слот, започващ след него
   * („да не изчезне без маркер"). Тя отпада: след R11 категорията на слота е от активната
   * година, тоест решената му дата винаги пада в прозореца — състоянието, срещу което
   * клаузата пазеше, е недостижимо. Защитата живее в източника (R11) и в `resolveSlotPlan`,
   * а тук остава простото правило: нищо не се рисува извън прозореца.
   */
  const endIso = slotStartIso > fallbackEndIso ? windowEndIso : fallbackEndIso;

  // Рисуването започва не по-рано от прозореца — извън него нищо не се вижда, а всичко,
  // което се смята там, е непотиснато от ваканции и без обяснение.
  const drawFrom = slotStartIso > windowStartIso ? slotStartIso : windowStartIso;
  if (drawFrom > endIso) return [];

  const [cy, cm] = drawFrom.split('-').map(Number);
  const cursor = new Date(cy, cm - 1, 1);
  const [ey, em, ed] = endIso.split('-').map(Number);
  const end = new Date(ey, em - 1, ed);

  const out: string[] = [];
  while (cursor <= end) {
    for (const iso of getDatesForDayOfWeek(dayOfWeek, cursor.getFullYear(), cursor.getMonth())) {
      if (iso < drawFrom || iso > endIso) continue;
      if (isNonWorkingDate(iso, nonWorking)) continue;
      out.push(iso);
    }
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return out;
}

/**
 * Къде да застане маркерът „този слот няма нито едно занятие в показания период" (R9).
 *
 * 🔴 Слот с ВАЛИДНО начало никога не остава без нито един знак на екрана. Измерено след R8:
 * начало 2027-08-31 (последният ден от прозореца, тоест валидно) при слот в събота дава нула
 * занятия и по броящия, и по резервния път — денят от седмицата просто не се пада повече.
 * Празен календар БЕЗ обяснение е забранен от инварианта на модула; празен С маркер е
 * позволен и е точно сигналът, че нещо в данните иска внимание.
 *
 * Маркерът стои на началото на слота, когато то се вижда, иначе на началото на прозореца —
 * никога никъде другаде, за да не се чете като занятие.
 */
export function emptySlotMarkerDate(slotStartIso: string, window: DateWindow): string {
  if (slotStartIso >= window.startDate && slotStartIso <= window.endDate) return slotStartIso;
  return window.startDate;
}

/**
 * Прозорецът на календара за дадена учебна ГОДИНА.
 *
 * Ключът е първият източник, часовникът — само резервен. Данните (ваканции,
 * слотове) се теглят по съхранената активна година; прозорец, изведен от
 * `new Date()`, се разминава с нея при всяка смяна на годината и изрязва всичко
 * изтеглено. `today` остава за случая „стар backend, който не връща година" —
 * това е fail-open клонът, не работният път.
 */
export function calendarWindowFrom(
  schoolYear: string | null | undefined,
  today: Date,
  /**
   * Реалното начало на годината или на най-ранния курс (ISO), когато е известно.
   *
   * Прозорецът само се РАЗШИРЯВА назад, никога не се свива: учебната година може да
   * започва през август (границата на owner е 1 авг – 31 окт), и прозорец от 1 септември
   * би изрязал първите занятия. Обратното — прозорец, свит до късен курс — би скрил
   * есенните ваканции от календара, затова се взима по-РАННАТА от двете дати.
   *
   * 🔴 Разширяването е ОГРАНИЧЕНО до 1 юли на първата календарна година (долната граница
   * на работната година). Без ограничението слот с дата от ДРУГА година — заварен запис
   * или още незасегнат от смяната — разтягаше прозореца цяла година назад и изтегляше в
   * календара ваканциите на предишната. Хванато от съществуващ тест, не от ревю.
   */
  earliestStart?: string | null,
): DateWindow {
  const fromKey = schoolYearStartDate(schoolYear);
  const fromClock = toIsoDate(schoolYearBounds(today).start);
  const anchor = fromKey ?? fromClock;
  const floor = absoluteWorkYearWindow(schoolYear)?.startDate ?? null;
  const fromData =
    earliestStart &&
    /^\d{4}-\d{2}-\d{2}$/.test(earliestStart) &&
    earliestStart < anchor &&
    (floor === null || earliestStart >= floor)
      ? earliestStart
      : null;
  if (fromData) {
    // Само НАЧАЛОТО се мести. `dateWindowFrom(fromData)` би преместил и края със същото
    // отместване (2026-08-15 → край 2027-08-14 вместо 2027-08-31), тоест „разширяването"
    // щеше да изреже последните две седмици на август — точно обратното на обещаното.
    const end = dateWindowFrom(anchor)?.endDate;
    if (end) return { startDate: fromData, endDate: end };
  }
  // Никакъв `new Date()` тук: този модул нарочно чете часовника само през
  // `opts.today` (виж докблока горе), иначе клонът е нетестваем. И двата входа
  // са конструирани, не въведени — резервът е за счупен `today`.
  return dateWindowFrom(fromKey ?? fromClock) ?? dateWindowFrom(fromClock) ?? {
    startDate: fromClock,
    endDate: fromClock,
  };
}

/**
 * Пада ли датата в неучебен период.
 *
 * СЪЩОТО правило, което ползва `lessonDatesForSlot` — сравнение на ISO низове
 * срещу диапазоните, без разгъване. Разгънато множество тук би се разминало с
 * изчислителя: `expandHolidayDates` реже на 400 дни ОТ НАЧАЛОТО на диапазона,
 * тоест при сгрешена НАЧАЛНА година („2020-12-24 … 2026-12-26" минава
 * валидацията, която проверява само `endDate ≥ startDate") множеството спира
 * през 2022 и не стига до реалната ваканция, а изчислителят я вижда. Тогава
 * резервният път рисува часове в дни, в които планираният ги маха.
 *
 * Разгъването остава само за МАРКЕРИТЕ на ваканциите — там таванът пази таблото
 * от четвърт милион събития при сбъркана крайна година.
 */
export function isNonWorkingDate(iso: string, nonWorking: NonWorkingRange[]): boolean {
  return nonWorking.some((r) => r.startDate && r.endDate && iso >= r.startDate && iso <= r.endDate);
}

/** Етикет на един час: „3Б · 9:00–10:30 · Стая 1". */
export function slotLabel(slot: TeacherScheduleSlotView, assistantSuffix?: string): string {
  const parts = [slot.className, `${slot.startTime}–${slot.endTime}`];
  if (slot.roomName) parts.push(slot.roomName);
  if (slot.isAssistant && assistantSuffix) parts.push(assistantSuffix);
  return parts.join(' · ');
}

/**
 * Часовете и ваканциите на учителя като събития за `CalendarWidget`.
 *
 * Слот с известен брой занятия (`weeksPerYear` от категорията) спира на
 * ПОСЛЕДНОТО си занятие — дори то да падне след 30 юни. Дотогава календарът
 * разгъваше всичко до края на прозореца и обещаваше ~512 занятия на production,
 * които няма да се проведат (2026-09-04). Слот БЕЗ брой пази старото поведение:
 * разгъване до края на прозореца.
 *
 * Инвариантът за грешките: празен календар БЕЗ обяснение е забранен. Затова
 * слот, чийто план не важи, пада на разгъването вместо да изчезне. Празен
 * календар С маркер на всеки ден е позволен и е точно сигналът, че данните за
 * ваканциите са счупени — админът вижда защо няма часове.
 */
export function buildTeacherCalendarEvents(
  slots: TeacherScheduleSlotView[],
  holidays: TeacherHolidayView[],
  opts: TeacherCalendarOptions,
): CalendarEvent[] {
  const events: CalendarEvent[] = [];

  /**
   * Най-ранната РЕШЕНА начална дата сред слотовете (R2).
   *
   * Сървърът я връща за всеки слот (изключение на категорията → настройките → 1 септември
   * от ключа); клиентът няма входовете да си я изведе. Само тя може да отмести прозореца
   * назад — например когато учебната година започва през август.
   */
  const earliestSlotStart = slots.reduce<string | null>((min, s) => {
    const d = s.startDate;
    if (!d) return min;
    return min === null || d < min ? d : min;
  }, null);

  const window = calendarWindowFrom(opts.schoolYear, opts.today, earliestSlotStart);
  const startIso = window.startDate;
  // Краят на резервния път остава 30 юни — поведението, което задача B закова (тестът
  // „часът извън учебната година не се разгъва"). Самото разгъване живее в
  // `fallbackLessonDates`, обща с админското табло: правилата му (праг по слота, преливане
  // на `setMonth`, горна граница) се разминаха, докато бяха преписани на две места.
  const [startYear] = startIso.split('-').map(Number);
  const end = new Date(startYear + 1, 5, 30);

  // ЕДИН проход: клипнатите периоди хранят И маркерите, И потискането по
  // резервния път, И изчислителя. Втори проход по суровите ваканции би върнал
  // дрейфа — при задача E тържествата биха потискали час, без да го обяснят.
  const palette = calendarPalette(opts.primaryColor);

  /**
   * 🔴 Неучебните периоди се клипват до обхвата, РЕШЕН НА СЪРВЪРА (C4 R5).
   *
   * Планът и клипването трябва да са една граница: клипвани към по-късия екранен прозорец,
   * ваканциите извън него ставаха невидими за плана — занятие падаше в непотисната ваканция, а
   * маркерът, който би го обяснил, беше изрязан.
   *
   * Границата вече идва ГОТОВА от `TeacherDashboardResponse.bounds`, вместо клиентът да я
   * смята. Не заради права — `firestore.rules` допуска `settings/general` на всеки член на
   * училището — а защото ВТОРА сметка се разминава при първата промяна в реда на предимство.
   * Измерено на съседен път: подсказката в „Категории" броеше спрямо работната година, а
   * блокирането спрямо учебната, и двете страни съвпадаха само там, където няма събота.
   *
   * ⚠️ Резервът НЕ е екранният прозорец. Той е точно дефектът, който R10 поправи: клипнати към
   * по-късия прозорец, ваканциите извън него стават невидими за плана и занятие пада в
   * непотисната ваканция (тестът „R10: няма занятие в непотисната ваканция" го заковава).
   * 🔴 Резервът е `absoluteWorkYearWindow` — доказуемо НАДМНОЖЕСТВО на всичко, което сървърът
   * може да върне, а не опит да съвпадне с него.
   *
   * `holidayWindow(година, null)` изглеждаше правилният резерв („същата функция"), но НЕ Е:
   * сървърът я вика с настройките, а тя е СЪЮЗ и взима максимума на краищата, тоест въведена
   * работна година мести края НАПРЕД. Резултат: учителят вижда ПО-МАЛКО от админа — точно
   * дефектът, който R5 съществува да затвори, влязъл през резерва.
   *
   * Надмножеството е проверимо: `holidayWindow` вече клипва кандидатите си до абсолютните
   * прозорци ПРИ ЧЕТЕНЕ, тоест твърдението важи за всяко съдържание на базата, не само за
   * стойности, минали през валидацията. Гардът е в `schoolCalendar.test.ts`.
   *
   * Цената, приета: при стар backend учителят може да види занятия отвъд периода с данни за
   * ваканции — старият дефект, но само в преходния прозорец и само в посока „повече", не
   * „по-малко". Двамата да гледат РАЗЛИЧЕН курс е по-лошо от единия да гледа по-дълъг.
   */
  const scope = opts.bounds?.scope ?? absoluteWorkYearWindow(opts.schoolYear) ?? window;
  const periods = nonWorkingPeriodsFrom(
    holidays.map((h) => ({
      startDate: h.startDate,
      endDate: h.endDate,
      name: h.name,
      kind: h.type,
    })),
    scope,
  );
  const nonWorking = periods.map((p) => p.range);

  // Ваканциите ПЪРВИ — датите им потискат часовете отдолу.
  for (const p of periods) {
    const event = nonWorkingEvent(p, palette, opts.kindLabels);
    for (const iso of expandHolidayDates(p.range)) {
      events.push({ ...event, date: iso });
    }
  }

  for (const slot of slots) {
    // Гардът за липсващ ден живее в `fallbackLessonDates` и `lessonDatesForSlot` — тук се
    // проверява само за да не се строи етикет напразно. `AdminPage` НЯМА своя проверка и
    // разчита на същите две функции; ако тази тук вършеше работата сама, таблото щеше да
    // трупа маркери там, където учителят не показва нищо.
    if (!slot.dayOfWeek) continue;
    const label = slotLabel(slot, opts.assistantSuffix);
    const role: CalendarRole = slot.isAssistant ? 'assistant' : 'lesson';

    // `?? null` е задължително: срещу още недеплойнат backend полето идва
    // `undefined`, а не null (виж коментара в `TeacherDashboardPage`).
    const weeks = slot.weeksPerYear ?? null;
    // 🔴 Планът се смята в границите на ГОДИНАТА (по ключа), не в прозореца на екрана —
    // иначе валидността му зависи от чужди слотове (виж `resolveSlotPlan`).
    const slotPlan = resolveSlotPlan({
      slotStartDate: slot.startDate,
      schoolYear: opts.schoolYear,
      yearStartDate: startIso,
      scopeEndDate: scope.endDate,
    });
    const planned =
      weeks === null || !slotPlan.inPlan || !slotPlan.endDate
        ? null
        : lessonDatesForSlot({
            dayOfWeek: slot.dayOfWeek,
            startDate: slotPlan.startDate,
            endDate: slotPlan.endDate,
            // Обявеният ден побеждава ваканцията (C4b R0) — иначе календарът не рисува занятие,
            // за което декларацията вече очаква часове.
            declaredSchoolDays: slot.declaredSchoolDays,
            lessonCount: weeks,
            nonWorking,
          });

    // Планът важи само когато изчислителят го обяви за ПЪЛЕН. Скъсен план значи
    // ударен таван, тоест диапазон гълта остатъка от годината (`createHoliday`
    // валидира само `endDate ≥ startDate` — сгрешена година минава). Тогава
    // последната намерена дата НЕ е последното занятие: да се реже календарът
    // по нея и тя да носи маркера значи да се покаже грешна истина. Флагът се
    // чете, не се пресмята тук — правилото живее на едно място.
    if (planned?.complete) {
      for (const iso of planned.dates) {
        const isLast = iso === planned.lastDate;
        // Помощническата роля НАДДЕЛЯВА над „последен час".
        // Обратното изглеждаше по-информативно, но при курс с ЕДНО занятие единствената
        // дата е и последната — тогава цветът „като помощник" не се появява никога, а
        // легендата продължава да го обещава. „Последен час" остава в етикета, тоест
        // новината не изчезва; изчезва само дублирането ѝ в цвета.
        const markRole: CalendarRole = role === 'assistant' ? 'assistant' : isLast ? 'lastLesson' : role;
        events.push({
          date: iso,
          label: isLast && opts.lastLessonSuffix ? `${label} · ${opts.lastLessonSuffix}` : label,
          color: palette[markRole],
          shape: markShape(markRole),
        });
      }
      continue;
    }

    // Резервният път е ОБЩ с админското табло — виж `fallbackLessonDates`.
    const fallbackDates = fallbackLessonDates({
      dayOfWeek: slot.dayOfWeek,
      slotStartIso: slotPlan.startDate,
      windowStartIso: scope.startDate,
      fallbackEndIso: toIsoDate(end),
      windowEndIso: scope.endDate,
      nonWorking,
    });
    for (const iso of fallbackDates) {
      // Потискането на неучебните дни е ВЪТРЕ в `fallbackLessonDates` — общо с таблото.
      events.push({ date: iso, label, color: palette[role], shape: markShape(role) });
    }

    // R9 — нула занятия НЕ значи нула знаци.
    if (fallbackDates.length === 0) {
      events.push({
        date: emptySlotMarkerDate(slotPlan.startDate, window),
        label: opts.noLessonsSuffix ? `${label} · ${opts.noLessonsSuffix}` : label,
        color: palette.noSchool,
        shape: markShape('noSchool'),
      });
    }
  }

  return events;
}
